import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, Download, Upload, MoreHorizontal, Users, ChevronDown, RefreshCw, ArrowUpRight, ArrowDownRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { setupAxios } from '../../auth';
import { useAuth } from '../../auth';
import { ACCOUNTS_PERMS } from '../core/permissions';
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext';
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

export default function AccountsSalesCustomers() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { state: globalState, vessels: globalVessels } = useGlobalFilter();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');

  const [customers, setCustomers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  const filters = ['All', 'Active', 'Inactive', 'Overdue', 'CRM'];

  const fetchCustomers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (globalState.company) {
        params.append('companyId', globalState.company.id.toString());
      }
      if (globalState.vessel) {
        params.append('vesselId', globalState.vessel.id.toString());
      } else if (globalState.company && globalVessels.length > 0) {
        globalVessels.forEach((v: any) => params.append('vesselId', v.id.toString()));
      }
      const response = await api.get(`/accounts/customers?${params.toString()}`);
      let data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) data = [];
      
      // Client-side fallback filtering
      if (globalState.vessel) {
        data = data.filter((c: any) => !c.vesselId || String(c.vesselId) === String(globalState.vessel!.id));
      } else if (globalState.company && globalVessels.length > 0) {
        const vIds = new Set(globalVessels.map((v: any) => String(v.id)));
        data = data.filter((c: any) => !c.vesselId || vIds.has(String(c.vesselId)));
      }
      
      setCustomers(data);
    } catch (err: any) {
      console.error('Failed to fetch customers:', err);
      setError('Failed to load customers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Stable key derived from vessel IDs — only changes when actual vessel list changes,
  // not on every context re-render (which would cause duplicate requests).
  const globalVesselIds = useMemo(
    () => globalVessels.map((v: any) => v.id).sort().join(','),
    [globalVessels]
  );

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.company?.id, globalState.vessel?.id, globalVesselIds]);

  useEffect(() => {
    const closeDropdowns = () => { setActiveDropdown(null); setDropdownPos(null); };
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);

  // Compute stats
  const totalCustomers = customers.length;
  const activeCustomers = customers.filter(c => c.status !== 'inactive').length;

  const safeNumber = (val: any): number => {
    let num = Number(val);
    if (isNaN(num)) {
      const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : '';
      num = Number(clean);
      if (isNaN(num)) num = 0;
    }
    return num;
  };

  const totalReceivables = customers.reduce((sum, c) => sum + safeNumber(c.receivables), 0);
  const totalOverdue = customers.reduce((sum, c) => sum + safeNumber(c.overdue), 0);

  // Format currency
  const formatCurrency = (val: any) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(val));
  };

  const stats = [
    { label: 'Total Customers', value: totalCustomers.toString(), change: '+0%', trend: 'up', color: 'blue', icon: Users },
    { label: 'Active Customers', value: activeCustomers.toString(), change: '+0%', trend: 'up', color: 'emerald', icon: Users },
    { label: 'Receivables', value: formatCurrency(totalReceivables), change: '+0%', trend: 'up', color: 'violet', icon: Users },
    { label: 'Overdue', value: formatCurrency(totalOverdue), change: '0%', trend: 'down', color: 'amber', icon: Users },
  ];

  // Filter customers based on search and active tab
  const filteredCustomers = customers.filter(c => {
    const isInactive = c.status === 'inactive';
    if (activeFilter === 'Active' && isInactive) return false;
    if (activeFilter === 'Inactive' && !isInactive) return false;
    if (activeFilter === 'Overdue' && safeNumber(c.overdue) <= 0) return false;
    if (activeFilter === 'CRM') return false; // Mock for now
    const searchLower = searchQuery.toLowerCase();
    const nameMatch = c.displayName?.toLowerCase().includes(searchLower) || c.firstName?.toLowerCase().includes(searchLower);
    const companyMatch = c.companyName?.toLowerCase().includes(searchLower);
    const emailMatch = c.email?.toLowerCase().includes(searchLower);
    return nameMatch || companyMatch || emailMatch;
  });

  const handleDownloadCSV = () => {
    if (filteredCustomers.length === 0) {
      alert("No customer data to export.");
      return;
    }
    const headers = ["Sr. No.", "Name", "Company", "Email", "Phone", "Receivables", "Status"];
    const rows = filteredCustomers.map((c, index) => [
      index + 1,
      c.displayName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || '-',
      c.companyName || '-',
      c.email || '-',
      c.workPhone || c.mobile ? `\t${c.workPhone || c.mobile}` : '-',
      formatCurrency(c.receivables || 0),
      c.status === 'inactive' ? 'Inactive' : 'Active'
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `customers_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">

        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>

        <main className="flex-1 flex flex-col h-full overflow-hidden relative">


          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5" id="main-scroll">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Accounts / Sales</div>
                <h1 className="text-xl font-bold text-slate-900 leading-tight">Customers</h1>
                <p className="text-xs text-slate-500">Manage and track all your customer relationships</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>Data refreshed just now</span>
                  <button onClick={fetchCustomers} className="p-1 rounded hover:bg-slate-100 transition-colors" title="Refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-blue-500' : ''}`} />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.CUSTOMER_CREATE) && (
                  <button
                    onClick={() => navigate('/accounts/sales/customers/new')}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Customer
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-xl border border-red-100 text-sm">
                {error}
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}>
                      <stat.icon className={`w-4.5 h-4.5 text-${stat.color}-600`} />
                    </div>
                    <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${stat.trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                      {stat.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {stat.change}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Table Card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Toolbar */}
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  {filters.map(f => (
                    <button
                      key={f}
                      onClick={() => setActiveFilter(f)}
                      className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${activeFilter === f ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                  </div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors" title="Filter">
                    <Filter className="w-4 h-4 text-slate-500" />
                  </button>
                  */}
                  <button
                    onClick={handleDownloadCSV}
                    className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                    title="Export"
                  >
                    <Download className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>

              {/* Table Header */}
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-175">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50">
                      <th className="px-4 py-3 font-medium text-center text-slate-400">Sr. No.</th>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Company</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Phone</th>
                      <th className="px-4 py-3 font-medium">Receivables</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="p-16 text-center">
                          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">Loading customers...</p>
                        </td>
                      </tr>
                    ) : filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={8}>
                          {/* Empty State */}
                          <div className="p-16 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5">
                              <Users className="w-10 h-10" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">
                              {searchQuery ? 'No matching customers' : 'No customers found'}
                            </h3>
                            <p className="text-slate-500 text-sm text-center max-w-sm mb-6">
                              {searchQuery
                                ? 'Try adjusting your search or filters to find what you are looking for.'
                                : 'Start building your customer base. Add your first customer to begin tracking sales, invoices, and receivables.'}
                            </p>
                            {!searchQuery && (
                              <div className="flex items-center gap-3">
                                {hasPermission(ACCOUNTS_PERMS.CUSTOMER_CREATE) && (
                                  <button
                                    onClick={() => navigate('/accounts/sales/customers/new')}
                                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                                  >
                                    <Plus className="w-4 h-4" />
                                    Create Customer
                                  </button>
                                )}
                                <button className="px-5 py-2.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl text-sm font-bold transition-all flex items-center gap-2">
                                  <Upload className="w-4 h-4" />
                                  Import CSV
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map((customer, index) => (
                        <tr key={customer.id} className="hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => hasPermission(ACCOUNTS_PERMS.CUSTOMER_UPDATE) && navigate(`/accounts/sales/customers/${customer.id}`)}>
                          <td className="px-4 py-3 text-slate-500 font-medium text-center" onClick={e => e.stopPropagation()}>
                            {index + 1}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-blue-600 hover:underline">{customer.displayName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || '-'}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-medium">
                            {customer.companyName || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {customer.email || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {customer.workPhone || customer.mobile || '-'}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-700">
                            {formatCurrency(customer.receivables || 0)}
                          </td>
                          <td className="px-4 py-3">
                            {customer.status === 'inactive' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500">
                                Inactive
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">
                                Active
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right relative">
                            <button
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100"
                              onClick={e => {
                                e.stopPropagation();
                                if (activeDropdown === customer.id) {
                                  setActiveDropdown(null);
                                  setDropdownPos(null);
                                } else {
                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  setDropdownPos({ top: rect.bottom + 4, left: rect.right - 160 });
                                  setActiveDropdown(customer.id);
                                }
                              }}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>


          </div>
        </main>
      </div>

      {/* Dropdown Portal — renders fixed so it escapes table overflow */}
      {activeDropdown !== null && dropdownPos && (() => {
        const customer = filteredCustomers.find(c => c.id === activeDropdown);
        if (!customer) return null;
        return (
          <div
            className="fixed w-40 bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.15)] border border-slate-100 py-1.5 z-[9999]"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
            onClick={e => e.stopPropagation()}
          >
            {hasPermission(ACCOUNTS_PERMS.CUSTOMER_UPDATE) && (
              <button
                className="w-full text-left px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                onClick={() => {
                  setActiveDropdown(null);
                  setDropdownPos(null);
                  navigate(`/accounts/sales/customers/${customer.id}`);
                }}
              >
                Edit Customer
              </button>
            )}
            {hasPermission(ACCOUNTS_PERMS.CUSTOMER_DELETE) && (
              <button
                className="w-full text-left px-4 py-2 text-[13px] font-medium text-red-600 hover:bg-red-50 transition-colors"
                onClick={async () => {
                  setActiveDropdown(null);
                  setDropdownPos(null);
                  if (window.confirm('Are you sure you want to deactivate this customer? They will be moved to the Inactive tab.')) {
                    try {
                      await api.delete(`/accounts/customers/${customer.id}`);
                      fetchCustomers();
                    } catch (err: any) {
                      console.error(err);
                      alert('Failed to deactivate customer');
                    }
                  }
                }}
              >
                Deactivate Customer
              </button>
            )}
          </div>
        );
      })()}

    </>
  );
}

